import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from '../../config/axiosConfig';
import { API_ENDPOINTS } from '../../config/api';
import { useToast } from '../../components/ToastProvider';
import ConfirmDialog from '../../components/ConfirmDialog';

const RecipeManagement = () => {
    const { restaurant } = useSelector(state => state.auth);
    const restaurantId = restaurant?._id;
    const addToast = useToast();
    const [recipes, setRecipes] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [ingredients, setIngredients] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingRecipe, setViewingRecipe] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, recipeId: null });
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [formData, setFormData] = useState({
        menuItemId: '',
        ingredients: []
    });

    useEffect(() => {
        fetchRecipes();
        fetchMenuItems();
        fetchIngredients();
    }, []);

    const fetchRecipes = async () => {
        try {
            const response = await axios.get(`${API_ENDPOINTS.INVENTORY_RECIPES}?restaurantId=${restaurantId}`);
            setRecipes(response.data);
        } catch (error) {
            console.error('Error fetching recipes:', error);
        }
    };

    const fetchMenuItems = async () => {
        try {
            const response = await axios.get(`${API_ENDPOINTS.MENU}?restaurantId=${restaurantId}`);
            setMenuItems(response.data);
        } catch (error) {
            console.error('Error fetching menu items:', error);
        }
    };

    const fetchIngredients = async () => {
        try {
            const response = await axios.get(`${API_ENDPOINTS.INVENTORY_INGREDIENTS}?restaurantId=${restaurantId}`);
            setIngredients(response.data);
        } catch (error) {
            console.error('Error fetching ingredients:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const dataWithRestaurantId = { ...formData, restaurantId };
            if (editingId) {
                await axios.put(API_ENDPOINTS.INVENTORY_RECIPE(editingId), dataWithRestaurantId);
                addToast('Recipe updated successfully!', 'success');
            } else {
                await axios.post(API_ENDPOINTS.INVENTORY_RECIPES, dataWithRestaurantId);
                addToast('Recipe created successfully!', 'success');
            }
            fetchRecipes();
            resetForm();
        } catch (error) {
            console.error('Error saving recipe:', error);
            addToast('Failed to save recipe', 'error');
        }
    };

    const addIngredient = () => {
        setFormData({
            ...formData,
            ingredients: [...formData.ingredients, { ingredientId: '', quantity: '' }]
        });
    };

    const updateIngredient = (index, field, value) => {
        const updatedIngredients = [...formData.ingredients];
        updatedIngredients[index][field] = value;
        setFormData({ ...formData, ingredients: updatedIngredients });
    };

    const removeIngredient = (index) => {
        const updatedIngredients = formData.ingredients.filter((_, i) => i !== index);
        setFormData({ ...formData, ingredients: updatedIngredients });
    };

    const handleView = (recipe) => {
        setViewingRecipe(recipe);
        setShowViewModal(true);
    };

    const handleEdit = (recipe) => {
        setFormData({
            menuItemId: recipe.menuItemId._id,
            ingredients: recipe.ingredients.map(ing => ({
                ingredientId: ing.ingredientId._id,
                quantity: ing.quantity
            }))
        });
        setEditingId(recipe._id);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(API_ENDPOINTS.INVENTORY_RECIPE(id));
            addToast('Recipe deleted successfully!', 'success');
            fetchRecipes();
        } catch (error) {
            console.error('Error deleting recipe:', error);
            addToast('Failed to delete recipe', 'error');
        }
    };

    const resetForm = () => {
        setFormData({
            menuItemId: '',
            ingredients: []
        });
        setEditingId(null);
        setShowForm(false);
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await Promise.all([
                fetchRecipes(),
                fetchMenuItems(),
                fetchIngredients()
            ]);
            addToast('Data refreshed successfully!', 'success');
        } catch (error) {
            console.error('Error refreshing data:', error);
            addToast('Failed to refresh data', 'error');
        } finally {
            setIsRefreshing(false);
        }
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                <h2 className="text-base font-bold text-stone-800">Recipes</h2>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        className="inline-flex items-center justify-center p-2 rounded-lg font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-60 transition-colors shadow-sm size-9"
                        title="Refresh"
                        aria-label="Refresh"
                    >
                        {isRefreshing ? (
                            <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <span className="text-sm">↻</span>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowForm(true)}
                        className="h-9 px-3 rounded-lg text-xs font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                    >
                        Add Recipe
                    </button>
                </div>
            </div>

            {showForm && (
                <div className="bg-white p-3 sm:p-4 rounded-xl border border-stone-200 shadow-sm mb-4">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-2">
                        {editingId ? 'Edit Recipe' : 'Add New Recipe'}
                    </p>
                    <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Menu Item</label>
                            <select
                                value={formData.menuItemId}
                                onChange={(e) => setFormData({...formData, menuItemId: e.target.value})}
                                className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none bg-white"
                                required
                            >
                                <option value="">Select menu item</option>
                                {menuItems.map(item => (
                                    <option key={item._id} value={item._id}>
                                        {item.itemName} — ₹{item.itemPrice}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="mb-3">
                            <div className="flex justify-between items-center mb-1.5">
                                <label className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">Ingredients</label>
                                <button
                                    type="button"
                                    onClick={addIngredient}
                                    className="px-2 py-1 text-[10px] font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600"
                                >
                                    Add Ingredient
                                </button>
                            </div>
                            {formData.ingredients.map((ingredient, index) => (
                                <div key={index} className="flex flex-wrap items-center gap-2 mb-2">
                                    <select
                                        value={ingredient.ingredientId}
                                        onChange={(e) => updateIngredient(index, 'ingredientId', e.target.value)}
                                        className="flex-1 min-w-[120px] px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none bg-white"
                                        required
                                    >
                                        <option value="">Select ingredient</option>
                                        {ingredients.map(ing => (
                                            <option key={ing._id} value={ing._id}>
                                                {ing.name} ({ing.unit})
                                            </option>
                                        ))}
                                    </select>
                                    <input
                                        type="number"
                                        step="0.01"
                                        placeholder="Qty"
                                        value={ingredient.quantity}
                                        onChange={(e) => updateIngredient(index, 'quantity', e.target.value)}
                                        className="w-20 px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeIngredient(index)}
                                        className="px-2 py-2 text-xs font-semibold rounded-lg text-red-600 hover:bg-red-50"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="flex gap-2">
                            <button
                                type="submit"
                                className="px-3 py-2 text-xs font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600"
                            >
                                {editingId ? 'Update' : 'Add'} Recipe
                            </button>
                            <button
                                type="button"
                                onClick={resetForm}
                                className="px-3 py-2 text-xs font-semibold rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[800px] text-xs">
                        <thead className="bg-stone-50">
                            <tr>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Menu Item</th>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Selling</th>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600 hidden md:table-cell">Cost</th>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Margin</th>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600 hidden lg:table-cell">Ingredients</th>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recipes.map((recipe) => {
                                const sellingPrice = recipe.menuItemId?.itemPrice || 0;
                                const profitAmount = sellingPrice - recipe.costPrice;
                                const profitPercentage = sellingPrice > 0 ? ((profitAmount / sellingPrice) * 100).toFixed(1) : 0;
                                return (
                                    <tr key={recipe._id} className="border-t border-stone-100 hover:bg-stone-50/50">
                                        <td className="px-3 py-2 font-medium text-stone-800">{recipe.menuItemId?.itemName}</td>
                                        <td className="px-3 py-2 tabular-nums">₹{sellingPrice}</td>
                                        <td className="px-3 py-2 tabular-nums hidden md:table-cell">₹{recipe.costPrice?.toFixed(2)}</td>
                                        <td className="px-3 py-2">
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                                profitPercentage > 30 ? 'bg-emerald-100 text-emerald-800' :
                                                profitPercentage > 15 ? 'bg-amber-100 text-amber-800' :
                                                'bg-red-100 text-red-800'
                                            }`}>
                                                {profitPercentage}%
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 hidden lg:table-cell text-stone-600">
                                            {recipe.ingredients?.map((ing, idx) => (
                                                <div key={idx}>{ing.ingredientId?.name}: {ing.quantity} {ing.ingredientId?.unit}</div>
                                            ))}
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex gap-2">
                                                <button type="button" onClick={() => handleView(recipe)} className="text-orange-600 hover:text-orange-700 font-medium">View</button>
                                                <button type="button" onClick={() => handleEdit(recipe)} className="text-orange-600 hover:text-orange-700 font-medium">Edit</button>
                                                <button type="button" onClick={() => setConfirmDialog({ isOpen: true, recipeId: recipe._id })} className="text-red-600 hover:text-red-700 font-medium">Delete</button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* View Recipe Modal */}
            {showViewModal && viewingRecipe && (
                <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4" onClick={() => setShowViewModal(false)}>
                    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col border border-stone-200 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-3 sm:px-4 py-2 border-b border-stone-200 bg-stone-50/80">
                            <h3 className="text-base font-bold text-stone-800">Recipe Details</h3>
                            <button type="button" onClick={() => setShowViewModal(false)} className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-200 text-lg leading-none">×</button>
                        </div>
                        <div className="overflow-y-auto flex-1 p-3 sm:p-4 space-y-3">
                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-0.5">Menu Item</p>
                                <p className="font-semibold text-stone-800">{viewingRecipe.menuItemId?.itemName}</p>
                                <p className="text-xs text-stone-600">₹{viewingRecipe.menuItemId?.itemPrice}</p>
                            </div>
                            <div className="p-2.5 rounded-lg bg-stone-100 border border-stone-200">
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Cost</p>
                                <div className="text-xs space-y-0.5">
                                    <p>Cost: ₹{viewingRecipe.costPrice?.toFixed(2)} · Profit: ₹{(viewingRecipe.menuItemId?.itemPrice - viewingRecipe.costPrice)?.toFixed(2)}</p>
                                    <p className="font-semibold text-orange-600">Margin: {(((viewingRecipe.menuItemId?.itemPrice - viewingRecipe.costPrice) / viewingRecipe.menuItemId?.itemPrice) * 100)?.toFixed(1)}%</p>
                                </div>
                            </div>
                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1.5">Ingredients</p>
                                <div className="space-y-1.5">
                                    {viewingRecipe.ingredients?.map((ing, idx) => (
                                        <div key={idx} className="flex justify-between items-center px-2.5 py-2 rounded-lg bg-orange-50/80 border border-orange-100 text-xs">
                                            <span className="font-medium text-stone-800">{ing.ingredientId?.name}</span>
                                            <span className="text-stone-600">{ing.quantity} {ing.ingredientId?.unit}</span>
                                            <span className="tabular-nums text-orange-600">₹{(ing.ingredientId?.purchasePrice * ing.quantity)?.toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="px-3 sm:px-4 py-2.5 border-t border-stone-200">
                            <button type="button" onClick={() => setShowViewModal(false)} className="w-full px-3 py-2 text-xs font-semibold rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50">Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm Delete Dialog */}
            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                onClose={() => setConfirmDialog({ isOpen: false, recipeId: null })}
                onConfirm={() => handleDelete(confirmDialog.recipeId)}
                title="Delete Recipe"
                message="Are you sure you want to delete this recipe? This action cannot be undone."
            />
        </div>
    );
};

export default RecipeManagement;